import { useMemo, useState } from 'react';
import { Segmented } from '../../components/data';
import { Empty, ErrorBanner, Field, Loading } from '../../components/ui';
import { downloadCsv, fileStamp, slugForFile } from '../../lib/download';
import { useHeldResource } from '../../lib/hooks';

interface Asset {
  id: number;
  name: string;
  hostname: string | null;
  referenceNumber: string | null;
  serialNumber: string | null;
  type: string | null;
  category: string | null;
  product: string | null;
  contact: string | null;
  lastUser: string | null;
  /* Device detail from the RMM's user-defined fields (routes/amAutotask.ts). */
  lastLoginBy: string | null;
  osName: string | null;
  cpuCount: string | null;
  cpuDetail: string | null;
  hardDisk: string | null;
  memory: string | null;
  ipAddress: string | null;
  location: string | null;
  installed: string | null;
  warrantyExpires: string | null;
  active: boolean;
  modified: string | null;
}

interface AssetsPayload {
  linked: boolean;
  companyName: string | null;
  assets: Asset[];
  fetchedUtc?: string;
}

const shortDay = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const isWindowsHome = (os: string | null) => !!os && /windows/i.test(os) && /\bhome\b/i.test(os);

/* Memory arrives as the RMM wrote it — "8 GB", "8192 MB", "7.9 GB" or a bare
   byte count — so it is read as gigabytes and rounded before comparing. */
const memoryGb = (mem: string | null) => {
  const m = mem?.replace(/,/g, '').match(/(\d+(?:\.\d+)?)\s*([kmgt]i?b?)?/i);
  if (!m) return null;
  const n = Number(m[1]);
  const unit = (m[2] ?? '').charAt(0).toLowerCase();
  if (unit === 't') return n * 1024;
  if (unit === 'g') return n;
  if (unit === 'm') return n / 1024;
  if (unit === 'k') return n / 1024 ** 2;
  return n >= 1024 ** 2 ? n / 1024 ** 3 : n >= 256 ? n / 1024 : n;
};
const is8Gb = (mem: string | null) => {
  const gb = memoryGb(mem);
  return gb !== null && Math.round(gb) === 8;
};

/**
 * The client's assets: its configuration items in Autotask, read live through
 * the client's Autotask company (the same link the review's ticket slides
 * use — see routes/amAutotask.ts). Nothing is stored here.
 *
 * Warranty is the column people come for, so an expired one is marked, and
 * one expiring within 90 days is called out in the summary.
 */
export function AmClientAssets({ clientId, clientName }: { clientId: string; clientName: string }) {
  /* Held until Refresh — switching tabs and back does not call Autotask again. */
  const data = useHeldResource<AssetsPayload>(`/am/clients/${clientId}/autotask/assets`);
  const [show, setShow] = useState<'active' | 'all'>('active');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');

  const all = data.data?.assets ?? [];
  const types = useMemo(() => [...new Set(all.map((a) => a.type).filter((t): t is string => !!t))].sort(), [all]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter(
      (a) =>
        (show === 'all' || a.active) &&
        (!type || a.type === type) &&
        (!q ||
          [a.name, a.hostname, a.serialNumber, a.referenceNumber, a.product, a.contact, a.lastUser, a.lastLoginBy, a.osName, a.cpuDetail, a.ipAddress, a.location]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))),
    );
  }, [all, show, type, search]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const soon = new Date(today);
  soon.setDate(soon.getDate() + 90);
  const warranty = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`) : null);
  const active = all.filter((a) => a.active);
  const expired = active.filter((a) => {
    const w = warranty(a.warrantyExpires);
    return w !== null && w < today;
  }).length;
  const expiring = active.filter((a) => {
    const w = warranty(a.warrantyExpires);
    return w !== null && w >= today && w <= soon;
  }).length;

  if (data.loading) return <Loading label="Reading assets from Autotask" />;
  if (data.error) return <ErrorBanner message={data.error} />;
  if (!data.data?.linked) {
    return (
      <Empty title="Not found in Autotask">
        <p className="muted">
          {clientName} could not be found in Autotask under its name here, so there are no assets to show. Importing it
          from Autotask (Account Management › Import from Autotask) matches the names.
        </p>
      </Empty>
    );
  }

  const exportCsv = () =>
    downloadCsv(`${slugForFile(clientName)}_assets_${fileStamp()}.csv`, [
      ['Name', 'Hostname', 'Type', 'Category', 'Product', 'Serial number', 'Reference number', 'Contact', 'Last user', 'Last login by', 'OS name', 'CPU count', 'CPU detail', 'Hard disk info', 'Memory size', 'IP address', 'Location', 'Installed', 'Warranty expires', 'Active'],
      ...rows.map((a) => [
        a.name, a.hostname, a.type, a.category, a.product, a.serialNumber, a.referenceNumber, a.contact,
        a.lastUser, a.lastLoginBy, a.osName, a.cpuCount, a.cpuDetail, a.hardDisk, a.memory,
        a.ipAddress, a.location, a.installed, a.warrantyExpires, a.active ? 'Yes' : 'No',
      ]),
    ]);

  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <h3>Assets</h3>
          <p className="sub">
            {active.length} active of {all.length} in Autotask for <strong>{data.data.companyName}</strong>
            {expired ? ` · ${expired} out of warranty` : ''}
            {expiring ? ` · ${expiring} warranty ending within 90 days` : ''}
            {data.data.fetchedUtc
              ? ` · read ${new Date(data.data.fetchedUtc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
              : ''}
          </p>
        </div>
        <div className="row wrap">
          <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
            {data.refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={rows.length === 0} onClick={exportCsv}>
            Export CSV
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="row wrap mb-4" style={{ alignItems: 'flex-end' }}>
          <Field label="Search">
            <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, serial, user, IP…" />
          </Field>
          <Field label="Type">
            <select className="select" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All types</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Show">
            <Segmented
              label="Show"
              options={[
                { id: 'active', label: `Active (${active.length})` },
                { id: 'all', label: `All (${all.length})` },
              ]}
              value={show}
              onChange={setShow}
            />
          </Field>
        </div>

        {rows.length === 0 ? (
          <p className="muted small">{all.length === 0 ? 'Autotask has no configuration items for this client.' : 'Nothing matches.'}</p>
        ) : (
          <div className="table-wrap">
            <table className="table am-assets-compact">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Type</th>
                  <th>Product</th>
                  <th>Serial number</th>
                  <th>User</th>
                  <th>Last login by</th>
                  <th>OS name</th>
                  <th>CPU count</th>
                  <th>CPU detail</th>
                  <th>Hard disk info</th>
                  <th>Memory size</th>
                  <th>Installed</th>
                  <th>Warranty</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const w = warranty(a.warrantyExpires);
                  const out = w !== null && w < today;
                  const near = w !== null && !out && w <= soon;
                  const home = isWindowsHome(a.osName);
                  const ram8 = is8Gb(a.memory);
                  return (
                    <tr
                      key={a.id}
                      className={[a.active ? '' : 'muted', home && ram8 ? 'am-flag-red' : ''].filter(Boolean).join(' ') || undefined}
                    >
                      <td>
                        <div className="strong">{a.name}</div>
                        <div className="xs muted">
                          {[a.hostname && a.hostname !== a.name ? a.hostname : null, a.ipAddress, a.location, a.active ? null : 'Inactive']
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </td>
                      <td>
                        {a.type ?? '—'}
                        {a.category && <div className="xs muted">{a.category}</div>}
                      </td>
                      <td>{a.product ?? '—'}</td>
                      <td className="mono xs">{a.serialNumber ?? '—'}</td>
                      <td>
                        {a.lastUser ?? a.contact ?? '—'}
                        {a.lastUser && a.contact && <div className="xs muted">{a.contact}</div>}
                      </td>
                      <td className="xs">{a.lastLoginBy ?? '—'}</td>
                      <td className={home ? 'xs am-flag-yellow' : 'xs'}>{a.osName ?? '—'}</td>
                      <td className="xs">{a.cpuCount ?? '—'}</td>
                      <td className="xs" title={a.cpuDetail ?? undefined}>
                        <div className="am-asset-detail">{a.cpuDetail ?? '—'}</div>
                      </td>
                      <td className="xs" title={a.hardDisk ?? undefined}>
                        <div className="am-asset-detail">{a.hardDisk ?? '—'}</div>
                      </td>
                      <td className={ram8 ? 'xs am-flag-yellow' : 'xs'}>{a.memory ?? '—'}</td>
                      <td className="xs">{shortDay(a.installed)}</td>
                      <td className="xs" style={out ? { color: 'var(--status-danger)', fontWeight: 600 } : undefined}>
                        {shortDay(a.warrantyExpires)}
                        {out && <div>Expired</div>}
                        {near && <div className="muted">Ends soon</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
